import { Element } from "@xmpp/xml"

export type GeocodedStop = {
  id: string
  found: boolean
  lat?: number
  lon?: number
  formattedAddress?: string
  street?: string
  locality?: string
  postalCode?: string
  country?: string
  countryCode?: string
}

const toNumber = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
  }
  return undefined
}

const toText = (value: unknown): string | undefined =>
  typeof value === "string" && value.length > 0 ? value : undefined

// Parses the geocode_stops reply: a JSON container with
// {"stops": [{id, found, lat, lng, formatted_address, ...}]} in input order.
export const parseGeocodeStops = (result: Element): GeocodedStop[] => {
  const json = result?.getChild("command")?.getChild("json", "urn:xmpp:json:0")
  if (!json) return []
  let payload: unknown
  try {
    payload = JSON.parse(json.text())
  } catch {
    return []
  }
  const stops =
    payload && typeof payload === "object" && "stops" in payload
      ? (payload as { stops?: unknown }).stops
      : undefined
  if (!Array.isArray(stops)) return []
  return stops.map((entry: unknown): GeocodedStop => {
    const raw: Record<string, unknown> =
      entry && typeof entry === "object"
        ? (entry as Record<string, unknown>)
        : {}
    const lat = toNumber(raw.lat)
    const lon = toNumber(raw.lng ?? raw.lon)
    const found = Boolean(raw.found) && lat !== undefined && lon !== undefined
    return {
      id: typeof raw.id === "string" ? raw.id : String(raw.id ?? ""),
      found,
      lat: found ? lat : undefined,
      lon: found ? lon : undefined,
      formattedAddress: toText(raw.formatted_address),
      street: toText(raw.street),
      locality: toText(raw.locality),
      postalCode: toText(raw.postal_code),
      country: toText(raw.country),
      countryCode: toText(raw.country_code),
    }
  })
}
